import { useRef, useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, X } from 'lucide-react'
import { describeError } from '@/lib/auth'
import { MAX_CUSTOM_STATUS } from '@/lib/presence'
import { socialApi } from '@/lib/socialApi'
import { cx } from '@/lib/cx'
import { usePresenceStore } from '@/store/presence'
import { useAuthStore } from '@/store/auth'
import { Modal } from './settings/Modal'
import { SelfCard } from './SelfCard'
import { EmojiButton } from './chat/EmojiButton'
import { ContextMenu, MenuRadio, MenuTitle } from './ContextMenu'
import { BadgeIcon } from './profile/BadgeIcon'
import styles from './CustomStatusModal.module.css'

const PLACEHOLDER = 'О чём думаешь?'
const MINUTE = 60_000

/** Через сколько свой статус уберётся сам — как у Discord; «keep» — оставить срок, который уже стоит. */
type Expiry = '24h' | '4h' | '1h' | '30m' | 'never' | 'keep'
const OPTIONS: { id: Exclude<Expiry, 'keep'>; label: string; ms: number | null }[] = [
  { id: '24h', label: '24 ч.', ms: 24 * 60 * MINUTE },
  { id: '4h', label: '4 ч.', ms: 4 * 60 * MINUTE },
  { id: '1h', label: '1 ч.', ms: 60 * MINUTE },
  { id: '30m', label: '30 мин.', ms: 30 * MINUTE },
  { id: 'never', label: 'Не удалять', ms: null },
]

const time = (d: Date) => d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })

/** «сегодня в 18:40», «завтра в 09:15», дальше — «3 октября в 12:00». */
function when(d: Date, now: Date): string {
  const days = Math.round((new Date(d).setHours(0, 0, 0, 0) - new Date(now).setHours(0, 0, 0, 0)) / (24 * 60 * MINUTE))
  if (days === 0) return `сегодня в ${time(d)}`
  if (days === 1) return `завтра в ${time(d)}`
  return `${d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })} в ${time(d)}`
}

/**
 * Свой статус, как «Настроить статус» в Discord: сверху своя карточка с пузырём — видно, как статус
 * будет выглядеть, ниже поле со смайликом, слева внизу — когда статус уберётся сам.
 */
export function CustomStatusModal({ onClose }: { onClose: () => void }) {
  const current = usePresenceStore((s) => s.customText)
  const currentUntil = usePresenceStore((s) => s.customUntil)
  const setCustomStatus = usePresenceStore((s) => s.setCustomStatus)
  const me = useAuthStore((s) => s.user)
  // значки — из своей карточки профиля: тот же запрос, что у неё, берётся из кэша
  const profile = useQuery({ queryKey: ['profile', me?.id], queryFn: () => socialApi.profile(me!.id), enabled: Boolean(me), staleTime: 60_000 })
  const keepUntil = current && currentUntil ? new Date(currentUntil) : null
  // новый статус по умолчанию на сутки, как у Discord; у заданного — его срок или «не удалять»
  const initial: Expiry = keepUntil ? 'keep' : current ? 'never' : '24h'
  const [text, setText] = useState(current)
  const [expiry, setExpiry] = useState<Expiry>(initial)
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)
  // подписи сроков считаем от открытия окна, а сам срок при сохранении — от момента нажатия
  const [openedAt] = useState(() => Date.now())
  const now = new Date(openedAt)
  const untilOf = (id: Expiry, from: number): Date | null => {
    if (id === 'keep') return keepUntil
    const ms = OPTIONS.find((o) => o.id === id)?.ms ?? null
    return ms === null ? null : new Date(from + ms)
  }
  const chosenUntil = untilOf(expiry, openedAt)
  const expiryLabel = chosenUntil ? `Удалить ${when(chosenUntil, now)}` : 'Не удалять'
  const trimmed = text.trim()
  // пустой текст без статуса сохранять нечего, даже если срок выбран заранее
  const unchanged = (trimmed === current && expiry === initial) || (!trimmed && !current)

  // эмодзи встаёт туда, где стоит курсор, а курсор — сразу после него
  const insert = (emoji: string) => {
    const el = input.current
    const start = el?.selectionStart ?? text.length
    const end = el?.selectionEnd ?? text.length
    const next = (text.slice(0, start) + emoji + text.slice(end)).slice(0, MAX_CUSTOM_STATUS)
    setText(next)
    requestAnimationFrame(() => {
      el?.focus()
      const at = Math.min(start + emoji.length, next.length)
      el?.setSelectionRange(at, at)
    })
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      // время нажатия берём из самого события: Date.now() в теле компонента линтер React не пускает
      await setCustomStatus(trimmed, trimmed ? untilOf(expiry, performance.timeOrigin + e.timeStamp) : null)
      onClose()
    } catch (err) {
      setError(describeError(err).message)
      setBusy(false)
    }
  }

  const badges = profile.data?.badges ?? []
  return (
    <Modal title="Свой статус" onClose={onClose} width={460}>
      <form className={styles.form} onSubmit={(e) => void submit(e)}>
        <SelfCard
          className={styles.preview}
          bubble={trimmed || PLACEHOLDER}
          placeholder={!trimmed}
          wrap
          extra={badges.length ? <span className={styles.badges}>{badges.map((b) => <BadgeIcon key={b.id} id={b.id} name={b.name} size={18} />)}</span> : null}
        />

        <label className={styles.label} htmlFor="custom-status">Статус</label>
        <div className={styles.field}>
          <EmojiButton value={null} onChange={(e) => (e?.name ? insert(e.name) : undefined)} label="Добавить эмодзи" className={styles.emoji} />
          <input
            ref={input}
            id="custom-status"
            className={styles.input}
            value={text}
            maxLength={MAX_CUSTOM_STATUS}
            autoFocus
            autoComplete="off"
            placeholder={PLACEHOLDER}
            onChange={(e) => setText(e.target.value)}
          />
          {text ? (
            <button type="button" className={styles.clear} aria-label="Стереть" data-tooltip="Стереть" onClick={() => { setText(''); input.current?.focus() }}>
              <X size={16} />
            </button>
          ) : null}
        </div>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}

        <div className={styles.footer}>
          <button
            type="button"
            className={cx(styles.expiry, menuAt && styles.expiryOpen)}
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAt)}
            // меню закрывается нажатием мимо себя; без этого нажатие на кнопку закрыло бы его и тут же открыло
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              if (menuAt) return setMenuAt(null)
              const r = e.currentTarget.getBoundingClientRect()
              setMenuAt({ x: r.left, y: r.bottom + 6 })
            }}
          >
            {expiryLabel} <ChevronDown size={16} className={styles.chevron} />
          </button>
          <button type="submit" className={styles.primary} disabled={busy || unchanged}>{busy ? 'Сохраняем…' : 'Сохранить'}</button>
        </div>
      </form>

      {menuAt ? (
        <ContextMenu x={menuAt.x} y={menuAt.y} label="Когда удалить свой статус" onClose={() => setMenuAt(null)}>
          <MenuTitle>Удалить через</MenuTitle>
          {OPTIONS.map((o) => {
            const at = untilOf(o.id, openedAt)
            return (
              <MenuRadio
                key={o.id}
                label={at ? `${o.label} (${when(at, now).replace('сегодня в ', '')})` : o.label}
                checked={expiry === o.id}
                onClick={() => { setExpiry(o.id); setMenuAt(null) }}
              />
            )
          })}
        </ContextMenu>
      ) : null}
    </Modal>
  )
}
